import { httpClient } from '@api/client/http';
import { tokenStorage } from '@api/lib/token-storage';

import type { ApiResponse, SendOtpData, VerifyOtpData } from '../types';

const BASE = '/workshop/auth';

// Re-export tokenStorage so feature-layer code doesn't need to know
// where the canonical storage module lives.
export { tokenStorage as refreshTokenStorage };

// ─── Auth API ─────────────────────────────────────────────────────────────────

export const authApi = {
  /**
   * Send OTP to a mobile number.
   * Backend validates user eligibility before sending.
   */
  sendOtp: async (mobileNumber: number): Promise<SendOtpData> => {
    const res = await httpClient.post<ApiResponse<SendOtpData>>(`${BASE}/send-otp`, {
      mobile_number: mobileNumber,
    });
    return res.data.data;
  },

  /**
   * Verify OTP.
   * Access token is inside data.userDetails.accessToken.
   * Refresh token is read from response body (or fallback to response header).
   */
  verifyOtp: async (
    verificationId: number,
    otp: number,
  ): Promise<{ data: VerifyOtpData; refreshToken: string | null }> => {
    const res = await httpClient.post<ApiResponse<VerifyOtpData>>(`${BASE}/verify-otp`, {
      verification_id: verificationId,
      otp,
    });
    const refreshToken =
      res.data?.data?.refreshToken ??
      res.data?.data?.userDetails?.refreshToken ??
      (res.headers['refresh-token'] as string | undefined) ??
      null;
    return { data: res.data.data, refreshToken };
  },

  /**
   * Resend OTP using an existing verification session.
   * Returns a new verification_id.
   */
  resendOtp: async (verificationId: number): Promise<SendOtpData> => {
    const res = await httpClient.post<ApiResponse<SendOtpData>>(`${BASE}/resend-otp`, {
      verification_id: verificationId,
    });
    return res.data.data;
  },

  /**
   * Exchange a refresh token for a new access token.
   * Refresh token is sent as a request header (not in body).
   * Called by the interceptors on 401; do not call directly from components.
   */
  refreshAccessToken: async (refreshToken: string): Promise<string> => {
    const res = await httpClient.post<
      ApiResponse<{ accessToken: string; refreshToken?: string | null }>
    >(`${BASE}/refresh-token`, {}, { headers: { 'refresh-token': refreshToken } });
    const newRt =
      res.data?.data?.refreshToken ?? (res.headers['refresh-token'] as string | undefined) ?? null;
    if (newRt && newRt !== 'null') {
      tokenStorage.setRefreshToken(newRt);
    }
    return res.data.data.accessToken;
  },

  /**
   * Fetch the current user's ERP permission codes from the backend.
   * Owner returns ['*']. Employee returns their assigned permission codes.
   * Call this when sessionStorage permissions are missing (new tab/browser open).
   */
  fetchPermissions: async (): Promise<string[]> => {
    const res = await httpClient.get<{ status: number; data: { permissions: string[] } }>(
      `${BASE}/permissions`,
    );
    return res.data.data.permissions;
  },

  /**
   * Logout the current device session.
   * Requires a valid access token (set by interceptors automatically).
   */
  logout: async (): Promise<void> => {
    await httpClient.post(`${BASE}/logout`);
  },
};
